import api from './api.service';
import type { ApiResponse } from '@/types';

function unwrap<T>(res: unknown): T | undefined {
  if (!res || typeof res !== 'object') return undefined;
  const r = res as ApiResponse<T>;
  if (r.data !== undefined) return r.data as T;
  return res as T;
}

export type CrmDemoType = 'Online' | 'Onsite' | 'POC';
export type CrmDemoStatus = 'Planned' | 'Done' | 'Cancelled';
export type CrmDemoPriority = 'High' | 'Medium' | 'Low';
export type CrmDemoFitStatus = 'Fit' | 'Partial' | 'Gap';
export type CrmDemoGapResolution = 'Configuration' | 'Customization' | 'Workaround' | 'Roadmap' | 'Not Planned';
export type CrmDemoRequirementStatus = 'Open' | 'In Progress' | 'Delivered' | 'Dropped';

export const CRM_DEMO_TYPES: CrmDemoType[] = ['Online', 'Onsite', 'POC'];
export const CRM_DEMO_PRIORITIES: CrmDemoPriority[] = ['High', 'Medium', 'Low'];
export const CRM_DEMO_FIT_STATUSES: CrmDemoFitStatus[] = ['Fit', 'Partial', 'Gap'];
export const CRM_DEMO_GAP_RESOLUTIONS: CrmDemoGapResolution[] = [
  'Configuration',
  'Customization',
  'Workaround',
  'Roadmap',
  'Not Planned',
];
export const CRM_DEMO_REQUIREMENT_STATUSES: CrmDemoRequirementStatus[] = ['Open', 'In Progress', 'Delivered', 'Dropped'];

export interface CrmDemoRequirement {
  id?: string;
  line_no?: number;
  requirement: string;
  module?: string | null;
  priority?: CrmDemoPriority;
  fit_status?: CrmDemoFitStatus;
  gap_resolution?: CrmDemoGapResolution | null;
  effort_estimate?: string | null;
  commitment?: string | null;
  committed_by?: string | null;
  committed_by_name?: string | null;
  due_date?: string | null;
  status?: CrmDemoRequirementStatus;
  follow_up_activity_id?: string | null;
  follow_up_status?: string | null;
  follow_up_date?: string | null;
}

export interface CrmDemoFitCounts {
  requirement_count: number;
  fit: number;
  partial: number;
  gap: number;
  open_commitments: number;
}

export interface CrmDemo extends CrmDemoFitCounts {
  id: string;
  demo_number: string;
  opportunity_id: string | null;
  lead_id: string | null;
  customer_id: string | null;
  title: string | null;
  demo_date: string;
  duration_minutes: number | null;
  demo_type: CrmDemoType;
  presenter_user_id: string | null;
  presenter_name: string | null;
  attendees: string | null;
  modules_demonstrated: string | null;
  status: CrmDemoStatus;
  overall_feedback: string | null;
  customer_rating: number | null;
  next_steps: string | null;
  completed_at: string | null;
  activity_id: string | null;
  opportunity: { id: string; opportunity_number: string; opportunity_name: string; phone?: string | null; email?: string | null } | null;
  lead: { id: string; lead_number: string; phone?: string | null; email?: string | null } | null;
  customer: { id: string; customer_code: string; customer_name: string; phone?: string | null; email?: string | null } | null;
  customer_name: string | null;
  requirements?: CrmDemoRequirement[];
}

export interface CrmDemoInput {
  opportunity_id?: string | null;
  lead_id?: string | null;
  customer_id?: string | null;
  title?: string | null;
  demo_date?: string;
  duration_minutes?: number | null;
  demo_type?: CrmDemoType;
  presenter_user_id?: string | null;
  attendees?: string | null;
  modules_demonstrated?: string | null;
  overall_feedback?: string | null;
  customer_rating?: number | null;
  next_steps?: string | null;
  requirements?: CrmDemoRequirement[];
}

export interface CrmDemoListFilters {
  status?: string;
  demo_type?: string;
  presenter_user_id?: string;
  opportunity_id?: string;
  lead_id?: string;
  customer_id?: string;
  date_from?: string;
  date_to?: string;
  has_gaps?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface CrmDemoListResponse {
  items: CrmDemo[];
  total: number;
  page: number;
  limit: number;
  totals: CrmDemoFitCounts & { done: number; planned: number };
}

function toParams(filters: object): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    out[k] = String(v);
  }
  return out;
}

const EMPTY_TOTALS = { requirement_count: 0, fit: 0, partial: 0, gap: 0, open_commitments: 0, done: 0, planned: 0 };

class CrmDemoService {
  async list(filters: CrmDemoListFilters): Promise<CrmDemoListResponse> {
    const res = await api.get('/crm/demos', { params: toParams(filters) });
    return unwrap<CrmDemoListResponse>(res) ?? { items: [], total: 0, page: 1, limit: 50, totals: EMPTY_TOTALS };
  }

  async getById(id: string): Promise<CrmDemo> {
    const res = await api.get(`/crm/demos/${id}`);
    const data = unwrap<CrmDemo>(res);
    if (!data) throw new Error('Demo not found');
    return data;
  }

  async create(body: CrmDemoInput): Promise<CrmDemo> {
    const res = await api.post('/crm/demos', body);
    const data = unwrap<CrmDemo>(res);
    if (!data) throw new Error('Failed to create demo');
    return data;
  }

  async update(id: string, body: CrmDemoInput): Promise<CrmDemo> {
    const res = await api.put(`/crm/demos/${id}`, body);
    return unwrap<CrmDemo>(res) as CrmDemo;
  }

  async complete(
    id: string,
    body: { overall_feedback: string; customer_rating?: number | null; next_steps?: string | null }
  ): Promise<CrmDemo> {
    const res = await api.post(`/crm/demos/${id}/complete`, body);
    return unwrap<CrmDemo>(res) as CrmDemo;
  }

  async cancel(id: string, reason: string): Promise<CrmDemo> {
    const res = await api.post(`/crm/demos/${id}/cancel`, { reason });
    return unwrap<CrmDemo>(res) as CrmDemo;
  }
}

export const crmDemoService = new CrmDemoService();
export default crmDemoService;
